import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { format, formatDistanceToNow } from 'date-fns';

import Page from '@/components/page/Page';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Bot,
  CheckCircle2,
  Loader2,
  XCircle,
  Clock,
  TrendingDown,
  Pause,
  AlertCircle,
  Search,
  Filter,
  FileText,
  User,
  CheckCircle,
} from 'lucide-react';

import { getBuyingAgents, type BuyingAgent } from './buyingAgentsStore';

const statusConfig: Record<
  BuyingAgent['status'],
  { bg: string; text: string; label: string; icon: React.ReactNode }
> = {
  running: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    label: 'Running',
    icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
  },
  waiting_approval: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    label: 'Awaiting Approval',
    icon: <Clock className="h-3.5 w-3.5" />,
  },
  completed: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    label: 'Completed',
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
  },
  failed: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    label: 'Failed',
    icon: <XCircle className="h-3.5 w-3.5" />,
  },
  paused: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    label: 'Paused',
    icon: <Pause className="h-3.5 w-3.5" />,
  },
};

export function AIWorkflowsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'all' | BuyingAgent['status']
  >('all');

  const filteredAgents = useMemo(() => {
    return getBuyingAgents()
      .filter((agent) => {
        const matchesSearch =
          agent.requestNumber
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          agent.product.toLowerCase().includes(searchQuery.toLowerCase()) ||
          agent.productCategory
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        const matchesStatus =
          statusFilter === 'all' || agent.status === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .sort(
        (a, b) =>
          new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
      );
  }, [searchQuery, statusFilter]);

  const stats = useMemo(() => {
    const agents = getBuyingAgents();
    const running = agents.filter(
      (a) => a.status === 'running' || a.status === 'waiting_approval',
    ).length;
    const completed = agents.filter((a) => a.status === 'completed').length;
    const needsAttention = agents.filter(
      (a) => a.status === 'waiting_approval' || a.status === 'failed',
    ).length;
    const totalSavings = agents
      .filter((a) => a.savings)
      .reduce((sum, a) => sum + (a.savings ?? 0), 0);
    return { running, completed, needsAttention, totalSavings };
  }, []);

  const handleViewDetails = (agentId: string) => {
    navigate({
      to: '/procurement-demo/ai-workflows/$agentId',
      params: { agentId },
    });
  };

  return (
    <Page
      title="Procurement Agents"
      subtitle="AI agents automatically sourcing and purchasing products"
    >
      <div className="flex flex-col gap-6">
        {/* Search and Filters */}
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              type="text"
              placeholder="Search by request number, product, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select
              value={statusFilter}
              onValueChange={(value) =>
                setStatusFilter(value as 'all' | BuyingAgent['status'])
              }
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="running">Running</SelectItem>
                <SelectItem value="waiting_approval">
                  Awaiting Approval
                </SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Overview */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              <div>
                <div className="text-xs text-muted-foreground">
                  Active Agents
                </div>
                <div className="text-xl font-semibold">{stats.running}</div>
              </div>
            </div>
            <div className="h-8 w-px bg-border" />
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <div>
                <div className="text-xs text-muted-foreground">
                  Completed Today
                </div>
                <div className="text-xl font-semibold">{stats.completed}</div>
              </div>
            </div>
            <div className="h-8 w-px bg-border" />
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <div>
                <div className="text-xs text-muted-foreground">
                  Needs Attention
                </div>
                <div className="text-xl font-semibold text-amber-600">
                  {stats.needsAttention}
                </div>
              </div>
            </div>
            <div className="h-8 w-px bg-border" />
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-green-600" />
              <div>
                <div className="text-xs text-muted-foreground">
                  Total Savings
                </div>
                <div className="text-xl font-semibold text-green-600">
                  ${stats.totalSavings.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Agents Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Request / Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Started At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Qty / Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Current Step
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Savings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredAgents.map((agent) => {
                  const status = statusConfig[agent.status];

                  return (
                    <tr
                      key={agent.id}
                      className="hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => handleViewDetails(agent.id)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-5 h-5 text-violet-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium flex items-center gap-2">
                              <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                              {agent.requestNumber}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {agent.product}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {agent.productCategory}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="text-sm text-muted-foreground cursor-default">
                              {formatDistanceToNow(new Date(agent.startedAt), {
                                addSuffix: true,
                              })}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            {format(new Date(agent.startedAt), 'MMM d, h:mm a')}
                          </TooltipContent>
                        </Tooltip>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium">
                          {agent.quantity.toLocaleString()} units
                        </div>
                        <div className="text-sm text-muted-foreground">
                          ${agent.estimatedValue.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 w-40">
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">
                              {agent.progress}%
                            </span>
                          </div>
                          <Progress
                            value={agent.progress}
                            className={`h-2 ${
                              agent.status === 'failed'
                                ? '[&>div]:bg-red-500'
                                : agent.status === 'completed'
                                  ? '[&>div]:bg-green-500'
                                  : agent.status === 'waiting_approval'
                                    ? '[&>div]:bg-amber-500'
                                    : ''
                            }`}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}
                        >
                          {status.icon}
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-muted-foreground max-w-[200px]">
                          {agent.currentStatusText}
                        </div>
                        {agent.assignedApprover &&
                          agent.status === 'waiting_approval' && (
                            <div className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                              <User className="w-3 h-3" />
                              {agent.assignedApprover}
                            </div>
                          )}
                      </td>
                      <td className="px-6 py-4">
                        {agent.savings ? (
                          <span className="text-sm font-semibold text-green-600">
                            -${agent.savings.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            —
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Button
                          variant="link"
                          className="p-0 h-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(agent.id);
                          }}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredAgents.length === 0 && (
            <div className="p-12 text-center">
              <p className="text-muted-foreground">
                No agents found matching your search.
              </p>
            </div>
          )}
        </Card>
      </div>
    </Page>
  );
}
